/**
 * @typedef {Object} IMapMarkerIconProps
 * @property {string=} color
 * @property {string=} innerColor
 * @property {string=} strokeColor
 * @property {[number, number]=} size
 * @property {number=} scale
 * @property {string|null=} imagePath
 * @property {maplibregl.PointLike=} imageIconOrigin
 * @property {maplibregl.PointLike=} imageIconInternalOrigin
 * @property {maplibregl.PointLike=} imageIconTopLeftAdditionalOffset
 * @property {maplibregl.PointLike=} imageIconBottomRightAdditionalOffset
 */

/**
 * @typedef {Object} IMapClusterIconAdditionalProps
 * @property {number=} itemCount
 * @property {((itemCount:number) => string|null)|string=} getColor
 * @property {((itemCount:number) => string|null)|string=} getStrokeColor
 * @property {((itemCount:number) => string|null)|string=} getTextColor
 * @property {((itemCount:number) => string|null)|string|null=} getItemCountAbbreviated
 * @typedef {IMapMarkerIconProps & IMapClusterIconAdditionalProps} IMapClusterIconProps
 */

window.OSM = window.OSM ?? {};
OSM.MapMarkerIcon = class MapMarkerIcon
{
    /** @type {IMapMarkerIconProps} */
    props;

    /** @type {[number, number]} */
    defaultSize = [27, 41];
    /** @type {[number, number]} */
    standardSize = [0, 0];
    /** @type {[number, number]} */
    size = [0, 0];

    scale = 1;
    standardBodyHeight = 33;
    bodyHeight = 0;

    color = "";
    innerColor = "";
    strokeColor = "";

    /** @type {HTMLElement} */
    element;

    /** @type {IMapProps} */
    coreConfig

    /**
     *
     * @param {IMapMarkerIconProps=} props
     * @param {boolean=} isExtended
     */
    constructor(props, isExtended) {
        this.props = props ?? {};
        this.coreConfig = OSM.MapConfigs.core ?? {};
        if(!isExtended){
            this.initialize();
        }
    }

    initialize(){
        this.applyProps();
        this.constructElement();
    }

    applyProps(){
        this.scale = this.props.scale ?? 1;

        this.standardSize = this.getScaledSize(1, this.props.size);
        this.size = this.getScaledSize(this.scale, this.props.size)

        this.bodyHeight = this.standardBodyHeight * this.scale;

        const notFoundColor =  "#FFFFFF";

        this.color = OSM.normalizeColor(this.props.color) ?? notFoundColor;
        this.innerColor = OSM.normalizeColor(this.props.innerColor) ?? notFoundColor;
        this.strokeColor = OSM.normalizeColor(this.props.strokeColor) ?? notFoundColor;
    }

    /**
     *
     * @param {number=} scale
     * @param {[number, number]=} fromSize
     * @returns {[number, number]}
     */
    getScaledSize(scale, fromSize) {

        scale = scale ?? 1;
        fromSize = fromSize ?? this.defaultSize;
        const sizeWidth = fromSize[0];
        const sizeHeight = fromSize[1];

        return [sizeWidth * scale, sizeHeight * scale];
    }

    /**
     *
     * @param {"bottom"|"top"|"right"|"left"|"center"} directionAnchor
     * @returns {maplibregl.Point}
     */
    getPopUpOffset(directionAnchor)
    {
        const sizeWidth = this.size[0];
        const sizeHeight = this.size[1];
        const origin = this.getOrigin() ?? new maplibregl.Point(0, 0)
        const internalOrigin = this.getInternalOrigin() ?? new maplibregl.Point(0, 0)
        const topLeftAdditionalOffset = this.getTopLeftAdditionalOffset() ?? new maplibregl.Point(0, 0)
        const bottomRightAdditionalOffset = this.getBottomRightAdditionalOffset() ?? new maplibregl.Point(0, 0)

        let yProp = origin.y;
        if(directionAnchor === "bottom"){
            yProp += -0.5;
        } else if(directionAnchor === "top"){
            yProp += 0.5;
        } else {
            yProp += internalOrigin.y;
        }

        let xProp = origin.x;
        if(directionAnchor === "right"){
            xProp += -0.5;
        } else if(directionAnchor === "left"){
            xProp += 0.5;
        } else {
            xProp += internalOrigin.x;
        }

        let yAdditionalOffset = 0;
        let xAdditionalOffset = 0;

        if(directionAnchor === "bottom"){
            yAdditionalOffset = topLeftAdditionalOffset.y;
        } else if(directionAnchor === "top"){
            yAdditionalOffset = bottomRightAdditionalOffset.y;
        } else if(directionAnchor === "right"){
            xAdditionalOffset = topLeftAdditionalOffset.x;
        } else if(directionAnchor === "left"){
            xAdditionalOffset = bottomRightAdditionalOffset.x;
        }

        const x = sizeWidth * xProp + xAdditionalOffset * this.scale;
        const y = sizeHeight * yProp + yAdditionalOffset * this.scale;

        return new maplibregl.Point(x, y)
    }

    /**
     *
     * @returns {maplibregl.Point|null}
     */
    getOrigin()
    {
        return new maplibregl.Point(0, -0.5);
    }

    /**
     *
     * @returns {maplibregl.Point|null}
     */
    getInternalOrigin()
    {
        return new maplibregl.Point(0, -0.1)
    }

    /**
     *
     * @returns {maplibregl.Point|null}
     */
    getTopLeftAdditionalOffset()
    {
        return new maplibregl.Point(-1, 4);
    }

    /**
     *
     * @returns {maplibregl.Point|null}
     */
    getBottomRightAdditionalOffset()
    {
        return new maplibregl.Point(1, 0);
    }

    /**
     *
     * @returns {HTMLElement}
     */
    constructElement()
    {
        const sizeWidth = this.size[0];
        const sizeHeight = this.size[1];
        const standardSizeWidth = this.standardSize[0];
        const standardSizeHeight = this.standardSize[1];

        const element = document.createElement("div");
        element.innerHTML = '<svg class="icon" display="block" width="' + sizeWidth + 'px" height="' + sizeHeight + 'px" viewBox="0 0 ' + standardSizeWidth + ' ' + standardSizeHeight + '" style="margin-top: -' + (this.bodyHeight / 2) + 'px;">\n' +
            '                <g fill-rule="nonzero">\n' +
            '                    <g transform="translate(3.0, 29.0)" fill="' + this.strokeColor + '">\n' +
            '                        <ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="10.5" ry="5.25002273"></ellipse>\n' +
            '                        <ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="10.5" ry="5.25002273"></ellipse>\n' +
            '                        <ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="9.5" ry="4.77275007"></ellipse>\n' +
            '                        <ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="8.5" ry="4.29549936"></ellipse>\n' +
            '                        <ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="7.5" ry="3.81822308"></ellipse>\n' +
            '                        <ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="6.5" ry="3.34094679"></ellipse>\n' +
            '                        <ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="5.5" ry="2.86367051"></ellipse>\n' +
            '                        <ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="4.5" ry="2.38636864"></ellipse>\n' +
            '                    </g>\n' +
            '                    <g fill="' + this.color + '">\n' +
            '                        <path d="M27,13.5 C27,19.074644 20.250001,27.000002 14.75,34.500002 C14.016665,35.500004 12.983335,35.500004 12.25,34.500002 C6.7499993,27.000002 0,19.222562 0,13.5 C0,6.0441559 6.0441559,0 13.5,0 C20.955844,0 27,6.0441559 27,13.5 Z">\n' +
            '\n' +
            '                        </path>\n' +
            '                    </g>\n' +
            '                    <g opacity="0.25" fill="' + this.strokeColor + '">\n' +
            '                        <path d="M13.5,0 C6.0441559,0 0,6.0441559 0,13.5 C0,19.222562 6.7499993,27 12.25,34.5 C13,35.522727 14.016664,35.500004 14.75,34.5 C20.250001,27 27,19.074644 27,13.5 C27,6.0441559 20.955844,0 13.5,0 Z M13.5,1 C20.415404,1 26,6.584596 26,13.5 C26,15.898657 24.495584,19.181431 22.220703,22.738281 C19.945823,26.295132 16.705119,30.142167 13.943359,33.908203 C13.743445,34.180814 13.612715,34.322738 13.5,34.441406 C13.387285,34.322738 13.256555,34.180814 13.056641,33.908203 C10.284481,30.127985 7.4148684,26.314159 5.015625,22.773438 C2.6163816,19.232715 1,15.953538 1,13.5 C1,6.584596 6.584596,1 13.5,1 Z">\n' +
            '\n' +
            '                        </path>\n' +
            '                    </g>\n' +
            '                    <g transform="translate(6.0, 7.0)" fill="' + this.innerColor + '">\n' +
            '\n' +
            '                    </g>\n' +
            '                    <g transform="translate(8.0, 8.0)">\n' +
            '                        <circle fill="' + this.strokeColor + '" opacity="0.25" cx="5.5" cy="5.5" r="5.4999962">\n' +
            '\n' +
            '                        </circle>\n' +
            '                        <circle fill="' + this.innerColor + '" cx="5.5" cy="5.5" r="5.4999962">\n' +
            '\n' +
            '                        </circle>\n' +
            '                    </g>\n' +
            '                </g>\n' +
            '            </svg>'
        element.remove();
        this.element = element;
        return element;
    }
}


OSM.MapMarkerImageIcon = class MapMarkerImageIcon extends OSM.MapMarkerIcon
{

    imageWidth = 0;
    imageHeight = 0;
    /** @type {maplibregl.Point|null} */
    imageIconOrigin = null;
    /** @type {maplibregl.Point|null} */
    imageIconInternalOrigin = null;
    /** @type {maplibregl.Point|null} */
    imageIconTopLeftAdditionalOffset = null;
    /** @type {maplibregl.Point|null} */
    imageIconBottomRightAdditionalOffset = null;

    /**
     *
     * @param {IMapMarkerIconProps} props
     */
    constructor(props) {
        super(props, true);
        this.props = props ?? {};

        this.initialize();
    }

    initialize() {
        super.initialize();
        this.determineSize()
    }

    applyProps() {
        super.applyProps();

        this.imageIconOrigin = this.props.imageIconOrigin ? OSM.requirePoint(this.props.imageIconOrigin) : null;
        this.imageIconInternalOrigin = this.props.imageIconInternalOrigin ? OSM.requirePoint(this.props.imageIconInternalOrigin) : null;
        this.imageIconTopLeftAdditionalOffset = this.props.imageIconTopLeftAdditionalOffset ? OSM.requirePoint(this.props.imageIconTopLeftAdditionalOffset) : null;
        this.imageIconBottomRightAdditionalOffset = this.props.imageIconBottomRightAdditionalOffset ? OSM.requirePoint(this.props.imageIconBottomRightAdditionalOffset) : null;
    }

    determineSize() {
        if(!this.props.imagePath){
            return;
        }
        const img = new Image();

        img.onload = () =>{
            this.imageWidth = img.width;
            this.imageHeight = img.height;
        }
        img.src = this.props.imagePath;
    }

    /**
     *
     * @returns {maplibregl.Point|null}
     */
    getOrigin()
    {
        return this.imageIconOrigin;
    }

    /**
     *
     * @returns {maplibregl.Point|null}
     */
    getInternalOrigin()
    {
        return this.imageIconInternalOrigin;
    }

    /**
     *
     * @returns {maplibregl.Point|null}
     */
    getTopLeftAdditionalOffset()
    {
        return this.imageIconTopLeftAdditionalOffset ?? new maplibregl.Point(-4, -4);
    }

    /**
     *
     * @returns {maplibregl.Point|null}
     */
    getBottomRightAdditionalOffset()
    {
        return this.imageIconBottomRightAdditionalOffset ?? new maplibregl.Point(4, 1);
    }

    constructElement() {

        const origin = this.getOrigin() ?? new maplibregl.Point(0, 0);
        const translateX = (100 * origin.x);
        const translateY = (100 * origin.y);

        const element = document.createElement("div");
        element.innerHTML = '<img class="icon" alt="icon" src="' + this.props.imagePath + '" />';
        element.querySelector('img').style.transform = "scale(" + this.scale + ")" + (translateX ? " translateX(" + translateX +"%)" : "" ) + (translateY ? " translateY(" + translateY +"%)" : "" );
        element.remove();
        this.element = element;
        return element;
    }
}

OSM.MapClusterIcon = class MapClusterIcon extends OSM.MapMarkerIcon
{
    /** @type {IMapClusterIconProps} */
    props;

    /** @type {[number, number]} */
    defaultSize = [58, 58];

    itemCount = 0;
    itemCountAbbreviated = "";
    textColor = "";

    /**
     *
     * @param {IMapClusterIconProps} props
     */
    constructor(props) {
        super(props, true);
        this.props = props ?? {};

        this.initialize();
    }


    applyProps(){
        super.applyProps();

        this.itemCount = this.props.itemCount ?? 0;

        /** @type {IMapClusterIconProps|null} */
        const clusterIconConfig = this.coreConfig.cluster?.clusterIcon ?? null
        /** @type {((itemCount:number) => string|null)|string|null} */
        const getColor = this.props.getColor ?? (clusterIconConfig?.getColor ?? null);
        /** @type {((itemCount:number) => string|null)|string|null} */
        const getStrokeColor = this.props.getStrokeColor ?? (clusterIconConfig?.getStrokeColor ?? null);
        /** @type {((itemCount:number) => string|null)|string|null} */
        const getTextColor = this.props.getTextColor ?? (clusterIconConfig?.getTextColor ?? null);
        /** @type {((itemCount:number) => string|null)|string|null} */
        const getItemCountAbbreviated =  this.props.getItemCountAbbreviated !== undefined ? this.props.getItemCountAbbreviated : (clusterIconConfig?.getItemCountAbbreviated ?? null);

        const notFoundColor =  "#FFFFFF";
        this.color = (getColor ? this.getColourValue(getColor, this.itemCount) : null) ?? notFoundColor;
        this.strokeColor = (getStrokeColor ? this.getColourValue(getStrokeColor, this.itemCount) : null) ?? notFoundColor;
        this.textColor = (getTextColor ? this.getColourValue(getTextColor, this.itemCount) : null) ?? notFoundColor;

        this.itemCountAbbreviated = (getItemCountAbbreviated ? getItemCountAbbreviated(this.itemCount) : null) ?? this.itemCount.toString();
    }

    /**
     *
     * @param {string|((itemCount:number) => string|null)} from
     * @param {number} itemCount
     * @returns {string|null}
     */
    getColourValue(from, itemCount)
    {
        let value;
        if(typeof from === 'function' ){
            value = from(itemCount);
        } else {
            value = from;
        }
        return OSM.normalizeColor(value);
    }


    /**
     *
     * @returns {maplibregl.Point|null}
     */
    getOrigin()
    {
        return null;
    }

    /**
     *
     * @returns {maplibregl.Point|null}
     */
    getInternalOrigin()
    {
        return null;
    }

    /**
     *
     * @returns {maplibregl.Point|null}
     */
    getTopLeftAdditionalOffset()
    {
        return new maplibregl.Point(9, 9);
    }

    /**
     *
     * @returns {maplibregl.Point|null}
     */
    getBottomRightAdditionalOffset()
    {
        return new maplibregl.Point(-9, -9);
    }

    /**
     *
     * @returns {HTMLElement}
     */
    constructElement()
    {
        const sizeWidth = this.size[0];
        const sizeHeight = this.size[1];
        const standardSizeWidth = this.standardSize[0];
        const standardSizeHeight = this.standardSize[1];

        const element = document.createElement("div");
        element.innerHTML = '<svg display="block" width="' + sizeWidth + 'px" height="' + sizeHeight + 'px" viewBox="0 0 ' + standardSizeWidth + ' ' + standardSizeHeight + '">\n' +
            '                <g fill-rule="nonzero">\n' +
            '                    <g class="icon" transform="translate(29.0, 29.0)">\n' +
            '                        <ellipse style="stroke-miterlimit: 14; stroke-linejoin: round; stroke-width: 4; paint-order: stroke markers; stroke-linecap: round; fill: ' + this.color + '; stroke: ' + this.strokeColor + '; stroke-opacity: 0.6;" rx="18" ry="18" />\n' +
            '                        <text x="0" y="0" style="fill: ' + this.textColor + '" text-anchor="middle" dy=".34em" font-size="0.8em">' + this.itemCountAbbreviated + '</text>\n' +
            '                    </g>\n' +
            '                </g>\n' +
            '                </svg>';
        element.remove();
        this.element = element;
        return element;
    }
}